import {
  INTENT_NOUN,
  INTENT_SYMBOL,
  type MessageIntent,
  type SharingMode,
} from '@/modules/template';
import { BASE64_RE as UUID80_BASE64_RE } from '@/uuid_extensions/uuid80';
import type { Contact, ContactList } from '@/modules/contact';
import { ContactLogic } from '@/modules/contact';
import type { MessageDraft, MessageHeader } from '@/modules/message';
import type { Topic } from '@/modules/topic/types';
import type { TopicAwarenessMap } from '@snowball/meprfo';

export { INTENT_SYMBOL };

export type ShareWizardStep =
  | 'recipients'
  | 'changes'
  | 'tone'
  | 'intro-message'
  | 'reminder-message'
  | 'update-message'
  | 'share-confirm'
  | 'final-share-confirm';

export type ShareWizardComposeStep =
  | 'intro-message'
  | 'reminder-message'
  | 'update-message';

const ALL_STEPS = [
  'recipients',
  'changes',
  'tone',
  'intro-message',
  'reminder-message',
  'update-message',
  'share-confirm',
  'final-share-confirm',
] as const satisfies readonly ShareWizardStep[];

const COMPOSE_STEPS = [
  'intro-message',
  'reminder-message',
  'update-message',
] as const satisfies readonly ShareWizardComposeStep[];

const STEP_TO_INTENT: Record<ShareWizardComposeStep, MessageIntent> = {
  'intro-message': 'intro',
  'reminder-message': 'reminder',
  'update-message': 'update',
};

const INTENT_TO_STEP: Record<MessageIntent, ShareWizardComposeStep> = {
  intro: 'intro-message',
  reminder: 'reminder-message',
  update: 'update-message',
};

/** The wizard's recipients grouped by the message intent each one will get. */
export type ContactsByIntent = Record<MessageIntent, Contact[]>;

/** What `routeRecipients` works out about the current recipient list. */
export interface RecipientRouting {
  contactsByIntent: ContactsByIntent;
  /**
   * localIds of recipients this session cannot message at all: the topic's
   * intro limit is reached and they would need an intro, or the sender asked
   * to skip people who have already seen the topic and they have.
   */
  introBlockedKeys: Set<string>;
  /** localId to intent — the inverse of `contactsByIntent`. */
  contactIntents: Map<string, MessageIntent>;
}

/** Everything the routing rule reads. */
export interface RecipientRoutingInput {
  recipients: ContactList;
  /** Recipients already sent to in an earlier leg of this session. */
  confirmedContactIds: ReadonlySet<string>;
  /** Server userId to what that person already knows about the topic. */
  awareness: TopicAwarenessMap;
  isUpdateFlow: boolean;
  introLimitReached: boolean;
  /** "Exclude recipients who've seen this topic before" (#581). */
  excludeAwareContacts: boolean;
}

/**
 * Split `recipients` into one leg per `maxRecipientsPerLeg`, composing each
 * leg's body against just that leg's recipients — a group text greets the
 * people it actually reaches, not everyone the intent covers.
 */
function fanOutLegs(
  topic: Topic,
  intent: MessageIntent,
  type: MessageHeader['type'],
  recipients: ContactList,
  getBody: (legRecipients: readonly Contact[]) => string,
  maxRecipientsPerLeg: number
): MessageDraft[] {
  const legs: MessageDraft[] = [];
  for (let i = 0; i < recipients.length; i += maxRecipientsPerLeg) {
    const legRecipients = recipients.slice(i, i + maxRecipientsPerLeg);
    legs.push({
      header: { topic, intent, type, recipients: legRecipients },
      body: getBody(legRecipients),
    });
  }
  return legs;
}

export const ShareWizardLogic = {
  stepForIntent(intent: MessageIntent): ShareWizardComposeStep {
    return INTENT_TO_STEP[intent];
  },

  composeSteps(): readonly ShareWizardComposeStep[] {
    return COMPOSE_STEPS;
  },

  allSteps(): readonly ShareWizardStep[] {
    return ALL_STEPS;
  },

  isStep(value: string): value is ShareWizardStep {
    return (ALL_STEPS as readonly string[]).includes(value);
  },

  // Informal name of a leg's lone recipient, used to personalize the
  // "Sent to …" status line. Returns '' for multi-recipient legs (callers
  // fall back to a count). Length capping lives in sentToLabel.
  soleRecipientName(recipients: ContactList): string {
    return recipients.length === 1
      ? ContactLogic.informalName(recipients[0])
      : '';
  },

  /**
   * Toast text for picks dropped because they turn out to have already seen the
   * topic (#581). Names everyone dropped so the sender can tell who left the
   * box, capping the list at three and counting the rest — a long name list in a
   * toast is unreadable, and the point is that something changed, not a roster.
   */
  alreadyIntroducedNotice(names: readonly string[]): string {
    if (names.length === 0) return '';
    const shown = names.slice(0, 3);
    const rest = names.length - shown.length;
    const listed =
      rest > 0
        ? `${shown.join(', ')} and ${rest} other${rest > 1 ? 's' : ''}`
        : shown.length > 1
          ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`
          : shown[0];
    const verb = names.length > 1 ? 'have' : 'has';
    return `${listed} ${verb} already been introduced to this topic.`;
  },

  // Status line for a sent leg. Personalizes to "Sent to 'Fred'" when there's
  // a single recipient, quoting the name so it stands out and truncating with
  // an ellipsis past 12 chars. Multi-recipient legs fall back to the
  // "N people" count.
  sentToLabel(count: number, soleRecipientName: string): string {
    const name = soleRecipientName.trim();
    if (name.length > 0) {
      const fitted = name.length > 12 ? `${name.slice(0, 11)}…` : name;
      return `Sent to '${fitted}'`;
    }
    return `Sent to ${count} ${count !== 1 ? 'people' : 'person'}`;
  },

  // Whether a group text is one of the ways this share could go out, which is
  // what the "Share as" segment offers a choice between. It only means
  // something when at least one intent has more than one recipient — otherwise
  // every leg is a single person, so group mode is indistinguishable from
  // singleton mode and the segment has nothing to offer.
  canGroupText(recipientCountsByIntent: readonly number[]): boolean {
    return recipientCountsByIntent.some(count => count > 1);
  },

  // Toolbar title for a compose step: "<symbol> <noun>", with a parenthetical
  // qualifier once the intent reaches more than one recipient.
  //   - 1 recipient                          → "<symbol> Intro"
  //   - >1, singleton mode (one DM each)     → "<symbol> Intros (DMs)"
  //   - >1, group mode, fits one leg       → "<symbol> Intros (grp txt)"
  //   - >1, group mode, needs several legs → "<symbol> Intros (grp txts)"
  composeStepTitle(
    intent: MessageIntent,
    recipientCount: number,
    singletonMode: boolean,
    maxRecipientsPerLeg: number
  ): string {
    const symbol = INTENT_SYMBOL[intent];
    const noun = INTENT_NOUN[intent];
    if (recipientCount <= 1) return `${symbol} ${noun.one}`;
    const base = `${symbol} ${noun.many}`;
    if (singletonMode) return `${base} (DMs)`;
    return recipientCount > maxRecipientsPerLeg
      ? `${base} (grp txts)`
      : `${base} (grp txt)`;
  },

  /**
   * Decide what message, if any, each recipient gets.
   *
   * This is the wizard's central rule. A recipient lands in exactly one of four
   * places — the intro, reminder or update list, or `introBlockedKeys` — and the
   * order of the branches below is the rule:
   *
   *   already sent to in this session  drop silently (they got their message)
   *   excluding aware, already aware   blocked (#581): the sender asked to
   *                                    reach only people new to the topic, so
   *                                    an aware contact is dropped outright
   *                                    rather than routed to the reminder they
   *                                    would otherwise get
   *   update flow                      aware and needing one, update; otherwise
   *                                    intro, because the update flow still
   *                                    introduces people who have never heard
   *                                    of the topic
   *   otherwise                        not aware, intro; aware, reminder
   *
   * An intro that the topic's intro limit forbids is blocked instead.
   *
   * Whether the sender is passing on a leg they received plays no part here: a
   * reshare routes each recipient by awareness exactly as a first share does
   * (#623). Provenance decides the leg type and the story, never the intent.
   */
  routeRecipients(input: RecipientRoutingInput): RecipientRouting {
    const {
      recipients,
      confirmedContactIds,
      awareness,
      isUpdateFlow,
      introLimitReached,
      excludeAwareContacts,
    } = input;
    const contactsByIntent: ContactsByIntent = {
      intro: [],
      reminder: [],
      update: [],
    };
    const introBlockedKeys = new Set<string>();

    const introduceOrBlock = (contact: Contact): void => {
      if (introLimitReached) introBlockedKeys.add(contact.localId);
      else contactsByIntent.intro.push(contact);
    };

    for (const contact of recipients) {
      if (confirmedContactIds.has(contact.localId)) continue;
      const userId = contact.snowballId ?? null;
      const topicAwareness = userId ? awareness.get(userId) : undefined;
      const introduced = topicAwareness?.introducedByAnyone ?? false;

      if (excludeAwareContacts && introduced) {
        introBlockedKeys.add(contact.localId);
      } else if (isUpdateFlow) {
        if (!introduced) {
          introduceOrBlock(contact);
        } else if (topicAwareness?.needsUpdate ?? true) {
          contactsByIntent.update.push(contact);
        }
      } else if (introduced) {
        contactsByIntent.reminder.push(contact);
      } else {
        introduceOrBlock(contact);
      }
    }

    const contactIntents = new Map<string, MessageIntent>();
    for (const [intent, contacts] of Object.entries(contactsByIntent) as [
      MessageIntent,
      Contact[],
    ][]) {
      for (const contact of contacts)
        contactIntents.set(contact.localId, intent);
    }

    return { contactsByIntent, introBlockedKeys, contactIntents };
  },

  // Recipient counts in compose-step order, the input the "Share as" segment
  // labels read.
  recipientCountsByIntent(contactsByIntent: ContactsByIntent): number[] {
    return COMPOSE_STEPS.map(
      step => contactsByIntent[STEP_TO_INTENT[step]].length
    );
  },

  // The intents this session writes a message for — the ones with anyone to
  // message — in compose-step order. The garden composer asks each one's
  // questions in this order, the same order the wizard walks its steps in.
  intentsWithRecipients(contactsByIntent: ContactsByIntent): MessageIntent[] {
    return COMPOSE_STEPS.map(step => STEP_TO_INTENT[step]).filter(
      intent => contactsByIntent[intent].length > 0
    );
  },

  // The intents this flow can write, in compose-step order, whoever has been
  // picked: an intro for anyone new to the topic, and for anyone who already
  // knows it an update in the update flow and a reminder outside it. A
  // reshare writes the same messages as a first share.
  flowIntents(args: { isUpdateFlow: boolean }): MessageIntent[] {
    return args.isUpdateFlow ? ['intro', 'update'] : ['intro', 'reminder'];
  },

  // The first compose step that has anyone to message. Null when no intent
  // has recipients, which is how the wizard knows there is nothing to compose.
  firstComposeStepWithContacts(
    contactsByIntent: ContactsByIntent
  ): ShareWizardComposeStep | null {
    return (
      COMPOSE_STEPS.find(
        step => contactsByIntent[STEP_TO_INTENT[step]].length > 0
      ) ?? null
    );
  },

  // The next compose step after `current` that has anyone to message, skipping
  // the empty ones. Null when `current` is the last one with recipients.
  nextComposeStepAfter(
    contactsByIntent: ContactsByIntent,
    current: ShareWizardComposeStep
  ): ShareWizardComposeStep | null {
    const startIdx = COMPOSE_STEPS.indexOf(current);
    for (let i = startIdx + 1; i < COMPOSE_STEPS.length; i++) {
      const step = COMPOSE_STEPS[i];
      if (contactsByIntent[STEP_TO_INTENT[step]].length > 0) return step;
    }
    return null;
  },

  /**
   * The steps this session actually shows, in order.
   *
   * A compose step appears only when its intent has recipients. A reshare goes
   * straight from the recipients step to its compose steps: its message is
   * fixed by the leg it passes on, so there is no tone to pick. The Changes
   * step, which re-presents the change list and collects the update summary
   * (#427), exists only in a fresh update: a reshared update passes on the
   * summary it received unchanged, and a sender who wants to say something
   * different starts their own sharing run with Share Updates.
   *
   * Once there is a send queue, one share-confirm step stands for each queued
   * leg and a final-share-confirm closes the flow.
   */
  visibleSteps(args: {
    contactsByIntent: ContactsByIntent;
    sharingMode: SharingMode;
    isUpdateFlow: boolean;
    sendQueueLength: number;
  }): readonly ShareWizardStep[] {
    const composeStepsWithContacts = COMPOSE_STEPS.filter(
      step => args.contactsByIntent[STEP_TO_INTENT[step]].length > 0
    );
    const base: readonly ShareWizardStep[] =
      args.sharingMode === 'reshare'
        ? ['recipients', ...composeStepsWithContacts]
        : [
            'recipients',
            ...(args.isUpdateFlow ? (['changes'] as const) : []),
            'tone',
            ...composeStepsWithContacts,
          ];
    if (args.sendQueueLength === 0) return base;
    const legSteps: ShareWizardStep[] = Array(args.sendQueueLength).fill(
      'share-confirm'
    );
    return [...base, ...legSteps, 'final-share-confirm'];
  },

  /**
   * The legs this session would send right now: one per intent with
   * recipients, fanned out to `maxRecipientsPerLeg` people each, in
   * compose-step order.
   *
   * Passing a leg on is a property of the leg rather than of what it says, so
   * the reshare type rides beside the intent and the per-intent loop needs no
   * branch for it.
   */
  buildSendPlan(args: {
    topic: Topic;
    contactsByIntent: ContactsByIntent;
    sharingMode: SharingMode;
    maxRecipientsPerLeg: number;
    composeBody: (
      intent: MessageIntent,
      legRecipients: readonly Contact[]
    ) => string;
  }): MessageDraft[] {
    const legType: MessageHeader['type'] =
      args.sharingMode === 'reshare' ? 'reshare' : 'share';
    return COMPOSE_STEPS.flatMap(step => {
      const intent = STEP_TO_INTENT[step];
      const contacts = args.contactsByIntent[intent];
      if (contacts.length === 0) return [];
      return fanOutLegs(
        args.topic,
        intent,
        legType,
        contacts,
        legRecipients => args.composeBody(intent, legRecipients),
        args.maxRecipientsPerLeg
      );
    });
  },

  /**
   * Whether the wizard's persisted state should be discarded rather than saved.
   *
   * A bootstrapped flow — a reshare, or an update — carries its signal (and
   * parent) in persisted state BEFORE anything is queued, and that signal is
   * exactly what a page refresh reads back to stay in the right flow. So only
   * discard when there is genuinely nothing to keep: no in-progress leg, an
   * empty send queue, no active-flow signal, AND no recipients selected.
   */
  shouldDiscardPersistedState(args: {
    inProgressLegId?: string | null;
    sendQueueLength: number;
    previousLegId?: string;
    aboutChangesSince?: string;
    recipientCount?: number;
    excludeAwareContacts?: boolean;
  }): boolean {
    const hasFlowSignal = !!args.previousLegId || !!args.aboutChangesSince;
    const hasRecipients = (args.recipientCount ?? 0) > 0;
    return (
      !args.inProgressLegId &&
      args.sendQueueLength === 0 &&
      !hasFlowSignal &&
      !hasRecipients &&
      !args.excludeAwareContacts
    );
  },

  // Drop any stored compose field value the current mode no longer offers — e.g.
  // a singleton-only greeting picked while texting individually, then the user
  // switched that intent to a group text. Values are keyed by field id;
  // `isStale` is the composer's own reading of whether the field declaring that
  // id still offers the value, from whichever dialect the topic composes in. A
  // key that is not a field id at all (a UUID80) can never be composed, so it is
  // dropped too. Returns the same object reference when nothing is dropped so
  // callers can skip a re-render.
  pruneStaleFieldValues(
    fieldValues: Readonly<Record<string, string>>,
    isStale: (fieldId: string, value: string) => boolean
  ): Record<string, string> {
    const next: Record<string, string> = {};
    for (const [key, value] of Object.entries(fieldValues)) {
      if (!UUID80_BASE64_RE.test(key) || isStale(key, value)) continue;
      next[key] = value;
    }
    return Object.keys(next).length === Object.keys(fieldValues).length
      ? (fieldValues as Record<string, string>)
      : next;
  },
};
